import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../api/client';
import { formatPrice } from '../../utils/formatPrice';

/**
 * Payment method icon components (inline SVG).
 */
function CashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-8 h-8"
    >
      <rect x="1" y="5" width="22" height="14" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M2 9h1M21 9h1M2 15h1M21 15h1" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-8 h-8"
    >
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
      <line x1="5" y1="15" x2="11" y2="15" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-8 h-8"
    >
      <path d="M3 21h18" />
      <path d="M3 10h18" />
      <path d="M12 3l9 7H3l9-7z" />
      <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
    </svg>
  );
}

const PAYMENT_ICONS = {
  cash: CashIcon,
  card: CardIcon,
  bank_transfer: BankIcon,
};

/**
 * Checkout modal component.
 *
 * Displays order summary, payment method selection, optional customer and note,
 * and handles order creation through the API.
 *
 * @param {Object}   props
 * @param {Object}   props.cart             - Cart data { items, subtotal, discountTotal, taxTotal, total }
 * @param {Object|null} props.customer      - Selected customer or null for guest
 * @param {Array}    props.paymentMethods   - Available payment methods [{ id, label }]
 * @param {Function} props.onComplete       - Called with the created order object on success
 * @param {Function} props.onClose          - Called when the modal should close
 * @param {Function} props.onChangeCustomer - Called when the user wants to change the customer
 */
export default function Checkout({
  cart,
  customer,
  paymentMethods,
  onComplete,
  onClose,
  onChangeCustomer,
}) {
  const [selectedMethod, setSelectedMethod] = useState(
    paymentMethods?.[0]?.id || 'cash'
  );
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Gift card state
  const [giftCardCode, setGiftCardCode] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [giftCardError, setGiftCardError] = useState('');

  // Collapsible section
  const [showGiftCard, setShowGiftCard] = useState(false);

  // Refs for auto-focus + blocking onscan.js interception
  const giftCardInputRef = useRef(null);

  // Auto-focus gift card input when section expands
  useEffect(() => {
    if (showGiftCard && !appliedGiftCard) {
      setTimeout(() => giftCardInputRef.current?.focus(), 50);
    }
  }, [showGiftCard, appliedGiftCard]);

  /**
   * Prevent onscan.js (attached to document) from intercepting paste and
   * keyboard-wedge scanner input on coupon/gift card fields.
   * onscan.js listens for 'keypress' and 'paste' on document — stopping
   * propagation on the input keeps those events from bubbling up.
   */
  const giftCardRef = useCallback((el) => {
    giftCardInputRef.current = el;
    if (el) {
      el.addEventListener('keypress', (e) => e.stopPropagation());
    }
  }, []);

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  // Computed discounts
  const giftCardCredit = appliedGiftCard
    ? Math.min(appliedGiftCard.balance, cart.total)
    : 0;

  const totalDue = Math.max(0, cart.total - giftCardCredit);

  const handleApplyGiftCard = useCallback(async () => {
    if (!giftCardCode.trim()) return;
    setGiftCardError('');
    setGiftCardLoading(true);
    try {
      const result = await api.checkGiftCard(giftCardCode.trim());
      if (result.valid) {
        setAppliedGiftCard({ code: result.code, balance: result.balance });
        setGiftCardCode('');
      } else {
        setGiftCardError(result.reason || 'Invalid gift card.');
      }
    } catch (err) {
      setGiftCardError(err?.data?.reason || err?.message || 'Failed to check gift card.');
    } finally {
      setGiftCardLoading(false);
    }
  }, [giftCardCode]);

  const handleRemoveGiftCard = useCallback(() => {
    setAppliedGiftCard(null);
    setGiftCardError('');
  }, []);

  const handleComplete = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const orderData = {
        items: cart.items.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
          ...(item.discountType && item.discountValue > 0
            ? { discount_type: item.discountType, discount_value: item.discountValue }
            : {}),
        })),
        payment_method: selectedMethod,
        customer_id: customer?.id || 0,
        note: note.trim() || undefined,
        gift_card_code: appliedGiftCard?.code || '',
        gift_card_amount: giftCardCredit,
      };

      const order = await api.createOrder(orderData);
      onComplete(order);
    } catch (err) {
      const message =
        err?.message || err?.data?.message || 'Failed to create order. Please try again.';

      // Attempt to extract stock-related error details
      const stockErrors = err?.data?.stock_errors || err?.stock_errors || null;

      setError({ message, stockErrors });
    } finally {
      setIsLoading(false);
    }
  }, [cart.items, selectedMethod, customer, note, onComplete, appliedGiftCard, giftCardCredit]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && !isLoading) {
        onClose();
      }
    },
    [isLoading, onClose]
  );

  const handleNoteChange = useCallback((e) => {
    setNote(e.target.value);
  }, []);

  const selectedLabel =
    paymentMethods?.find((m) => m.id === selectedMethod)?.label || selectedMethod;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Complete sale"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Complete Sale</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ---- Scrollable body ---- */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* -- Error display -- */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
              <div className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-800">{error.message}</p>
                  {error.stockErrors && Array.isArray(error.stockErrors) && (
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                      {error.stockErrors.map((stockErr, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{stockErr.name || stockErr.product_name}</span>
                          {': '}
                          {stockErr.message ||
                            `Only ${stockErr.available ?? stockErr.stock_quantity} available`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* -- Order summary -- */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Order Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>
                  Items ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                </span>
                <span>{formatPrice(cart.subtotal)}</span>
              </div>

              {cart.discountTotal > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-{formatPrice(cart.discountTotal)}</span>
                </div>
              )}

              {cart.taxTotal > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax</span>
                  <span>{formatPrice(cart.taxTotal)}</span>
                </div>
              )}

              {giftCardCredit > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Gift Card ({appliedGiftCard.code})</span>
                  <span>-{formatPrice(giftCardCredit)}</span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-base font-semibold text-gray-900">Total Due</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPrice(totalDue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* -- Gift card section -- */}
          <div>
            <button
              type="button"
              onClick={() => setShowGiftCard((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 hover:text-gray-700 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`w-4 h-4 transition-transform ${showGiftCard ? 'rotate-90' : ''}`}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Gift Card
            </button>
            {showGiftCard && (
              <div className="space-y-2">
                {appliedGiftCard ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm font-medium text-green-800 flex-1">
                      {appliedGiftCard.code}
                      <span className="text-green-600 ml-2">
                        Balance: {formatPrice(appliedGiftCard.balance)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveGiftCard}
                      className="text-green-600 hover:text-green-800 p-1"
                      aria-label="Remove gift card"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4V1m0 22v-3M4 12H1m22 0h-3" />
                          <rect x="5" y="5" width="14" height="14" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                          <line x1="9" y1="9" x2="9" y2="15" strokeLinecap="round" />
                          <line x1="12" y1="9" x2="12" y2="15" strokeLinecap="round" />
                          <line x1="15" y1="9" x2="15" y2="15" strokeLinecap="round" />
                        </svg>
                      </div>
                      <input
                        ref={giftCardRef}
                        type="text"
                        value={giftCardCode}
                        onChange={(e) => { setGiftCardCode(e.target.value); setGiftCardError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyGiftCard()}
                        onPaste={(e) => e.stopPropagation()}
                        placeholder="Scan barcode or type code"
                        disabled={giftCardLoading}
                        className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors disabled:opacity-50"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleApplyGiftCard}
                      disabled={giftCardLoading || !giftCardCode.trim()}
                      className="px-4 py-2 rounded-lg bg-pos-primary text-white text-sm font-medium hover:bg-pos-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {giftCardLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                )}
                {giftCardError && (
                  <p className="text-sm text-red-600">{giftCardError}</p>
                )}
              </div>
            )}
          </div>

          {/* -- Payment method selector -- */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Payment Method
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {(paymentMethods || []).map((method) => {
                const isActive = selectedMethod === method.id;
                const IconComponent = PAYMENT_ICONS[method.id] || CashIcon;

                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.id)}
                    disabled={isLoading}
                    className={`
                      flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all
                      ${
                        isActive
                          ? 'border-pos-primary bg-pos-secondary text-pos-primary-dark ring-2 ring-pos-primary/20'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                    aria-pressed={isActive}
                  >
                    <IconComponent />
                    <span className="text-xs font-medium">{method.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* -- Customer section -- */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Customer
            </h3>
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {customer ? customer.name : 'Guest'}
                  </p>
                  {customer?.email && (
                    <p className="text-xs text-gray-500">{customer.email}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onChangeCustomer}
                disabled={isLoading}
                className="text-sm text-pos-primary hover:text-pos-primary-dark font-medium transition-colors disabled:opacity-50"
              >
                Change
              </button>
            </div>
          </div>

          {/* -- Order note -- */}
          <div>
            <label
              htmlFor="checkout-order-note"
              className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 block"
            >
              Order Note
            </label>
            <textarea
              id="checkout-order-note"
              value={note}
              onChange={handleNoteChange}
              disabled={isLoading}
              placeholder="Add an optional note..."
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors resize-none disabled:opacity-50 disabled:bg-gray-50"
            />
          </div>
        </div>

        {/* ---- Footer ---- */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={isLoading}
            className="flex-[2] px-4 py-3 rounded-lg bg-pos-success text-white text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </>
            ) : (
              <>
                Complete Order — {formatPrice(totalDue)}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
